import { Range, Root, Thumb, Track } from '@radix-ui/react-slider'
import * as React from 'react'

import { styled } from '~/styled'
import { CSSWrapper } from '~/utilities/css-wrapper/CSSWrapper'

import { SliderSteps } from './SliderSteps'
import { SliderValue } from './SliderValue'

const StyledTrack = styled(Track, {
  base: [
    'rounded-full',
    'grow',
    'relative',
    'data-[orientation=horizontal]:h-1',
    'data-[orientation=vertical]:w-1'
  ],
  variants: {
    theme: {
      light: ['bg-white'],
      tonal: ['bg-grey-600']
    }
  }
})

const StyledSlider = styled(Root, {
  base: [
    'items-center',
    'flex',
    'relative',
    'touch-none',
    'select-none',
    'cursor-pointer',
    'data-[orientation=horizontal]:h-4',
    'data-[orientation=vertical]:flex-col',
    'data-[orientation=vertical]:w-4',
    'disabled:cursor-not-allowed',
    'disabled:opacity-30'
  ]
})

const StyledRange = styled(Range, {
  base: [
    'bg-primary-800',
    'rounded-full',
    'h-full',
    'absolute',
    'disabled:cursor-not-allowed',
    'disabled:opacity-30'
  ]
})

const StyledThumb = styled(Thumb, {
  base: [
    'bg-primary-900',
    'rounded-full',
    'block',
    'size-4',
    'hover:bg-primary-1000',
    'focus:outline-2',
    'focus:outline-offset-2',
    'focus:outline-primary-900',
    'focus:outline-solid',
    'disabled:cursor-not-allowed',
    'disabled:opacity-30'
  ]
})

export type SliderProps = React.ComponentProps<typeof StyledSlider> &
  Pick<React.ComponentProps<typeof StyledTrack>, 'theme'>

type SliderType = ReturnType<
  typeof React.forwardRef<HTMLSpanElement, SliderProps>
> & {
  Value: typeof SliderValue
  Steps: typeof SliderSteps
}

export const Slider = React.forwardRef<HTMLSpanElement, SliderProps>(
  (
    {
      value,
      defaultValue,
      min = 0,
      max = 100,
      theme = 'tonal',
      className,
      children,
      ...remainingProps
    },
    ref
  ) => {
    const values = value || defaultValue
    return (
      <CSSWrapper className={className}>
        <StyledSlider
          defaultValue={defaultValue}
          value={value}
          min={min}
          max={max}
          ref={ref}
          {...remainingProps}
        >
          <StyledTrack theme={theme}>
            <StyledRange />
          </StyledTrack>
          {values?.length &&
            values.map((_, i) => <StyledThumb key={`thumb${i}`} />)}
        </StyledSlider>
        {children}
      </CSSWrapper>
    )
  }
) as SliderType

Slider.Value = SliderValue
Slider.Steps = SliderSteps

Slider.displayName = 'Slider'
